"use client";

import Image from "next/image";
import {
  type ChangeEvent,
  type ComponentProps,
  type ForwardedRef,
  forwardRef,
  useState,
} from "react";

import { useTranslation } from "@/i18n/client";
import { cn } from "@/utils/shared";
import { Button } from "./button";

const AI_ICON_SIZE = 20;

type TTextareaProps = ComponentProps<"textarea"> & {
  hasCaption?: boolean;
  captionClassName?: string;
  hasAIAssistant?: boolean;
  hasWarning?: boolean;
  warningClassName?: string;
  warningMessage?: string;
  containerClassName?: string;
};

function TextareaComponent(
  { className, ...props }: TTextareaProps,
  ref: ForwardedRef<HTMLTextAreaElement>
): React.JSX.Element {
  const {
    hasCaption,
    hasAIAssistant,
    hasWarning,
    captionClassName,
    containerClassName,
    warningClassName,
    warningMessage,
    onChange,
    ...textareaProps
  } = props;
  const isControlled = textareaProps.value != null;
  const [uncontrolledLength, setUncontrolledLength] = useState(function getInitialLength(): number {
    return String(textareaProps.defaultValue ?? "").length;
  });
  let caption = uncontrolledLength;
  if (isControlled) {
    caption = String(textareaProps.value).length;
  }
  const { t } = useTranslation("global");

  function handleChange(event: ChangeEvent<HTMLTextAreaElement>): void {
    if (!isControlled) {
      setUncontrolledLength(event.currentTarget.value.length);
    }
    onChange?.(event);
  }

  function askAICopilot(): void {
    return;
  }

  return (
    <div className={cn("relative", containerClassName)}>
      <textarea
        className={cn(
          "text-ellipsis flex min-h-[60px] w-full rounded-xl border border-neutral-300 bg-transparent px-4 py-2.5 " +
            "text-neutral-600 text-sm transition-colors placeholder:text-neutral-600 " +
            "focus-visible:outline-hidden focus-visible:border-primary-600 focus-visible:ring-ring " +
            "disabled:text-neutral-400 disabled:bg-disabled-50 disabled:cursor-not-allowed disabled:opacity-50 " +
            "md:text-sm invalid:border-error-400 invalid:text-error-400 aria-invalid:border-error-400 aria-invalid:text-error-400 resize-y",
          className
        )}
        ref={ref}
        {...textareaProps}
        onChange={handleChange}
      />
      {hasAIAssistant && (
        <Button
          variant="nightfall"
          onClick={askAICopilot}
          size="icon"
          className="absolute right-2 left-auto top-auto bottom-10 py-2 pl-3 pr-4 w-auto"
        >
          <Image
            src="/images/logos/copilot-ai-white.svg"
            alt="AI Assistant"
            width={AI_ICON_SIZE}
            height={AI_ICON_SIZE}
          />
          {t("actions.improve-through-ai")}
        </Button>
      )}
      {hasCaption && textareaProps.maxLength && (
        <p className={captionClassName}>
          {caption} / {textareaProps.maxLength}
        </p>
      )}
      {hasWarning && (
        <p className={warningClassName}>{warningMessage || t("validation.fieldPattern")}</p>
      )}
    </div>
  );
}

const Textarea = forwardRef(TextareaComponent);
Textarea.displayName = "Textarea";

export { Textarea };
